import { Toaster } from '@/components/ui/toaster';
import { logger } from '@/lib/logger';
import { serviceWorkerManager } from '@/lib/serviceWorker';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SelectionProvider } from '@/contexts/SelectionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import type { LocalizationResource } from '@clerk/types';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { ClerkProtectedRoute } from '@/components/auth/ClerkProtectedRoute';
import { useState, useEffect, lazy, Suspense } from 'react';
import '@/styles/clerk-customizations.css';
import '@/styles/clerk-overrides.css';
import { initializeClerkModalCustomizer } from '@/utils/clerkModalCustomizer';
import { validateRequiredEnvVars, logEnvironmentStatus } from '@/utils/validateEnv';
import { validateSupabaseEnvironment } from '@/lib/validateSupabaseEnv';
import { PresentationModeProvider } from '@/contexts/PresentationModeContext';
import { DrawerProvider } from '@/contexts/DrawerContext';
import { LightingEffectsProvider } from '@/contexts/LightingEffectsContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { queryErrorHandler, shouldRetryQuery } from '@/lib/queryErrorHandler';
import { PageLoader } from '@/components/ui/PageLoader';
import { ServiceWorkerUpdatePrompt } from '@/components/ui/ServiceWorkerUpdatePrompt';
import { ErrorBoundary, AsyncErrorBoundary, RouteErrorBoundary } from '@/components/ErrorBoundary';

// Lazy load landing pages (not critical for app functionality)
const Index = lazy(() => import('./pages/Index'));
const LandingV2 = lazy(() => import('./pages/LandingV2'));
const LandingPreLaunch = lazy(() => import('./pages/LandingPreLaunch'));
const LandingVfloow = lazy(() => import('./pages/LandingVfloow'));

// Keep auth pages synchronous for better UX
import ClerkSignIn from './pages/ClerkSignIn';
import WaitlistSignup from './pages/WaitlistSignup';

// Lazy load main app pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Project = lazy(() => import('./pages/Project'));
const FloorPlanViewer = lazy(() => import('./pages/FloorPlanViewer'));
const ElevationEditor = lazy(() => import('./pages/ElevationEditor'));
const WorkspaceSettings = lazy(() => import('./pages/WorkspaceSettings'));
const Integrations = lazy(() => import('./pages/Integrations'));
const ProductLibrary = lazy(() => import('./pages/ProductLibrary'));
const PortalSettings = lazy(() => import('./pages/PortalSettings'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const PlatformAdmin = lazy(() => import('./pages/PlatformAdmin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

// Lazy load public and utility pages
const NotFound = lazy(() => import('./pages/NotFound'));
const PublicFloorPlanViewer = lazy(() => import('./pages/shared/PublicFloorPlanViewer'));
const EnvDebug = lazy(() => import('./pages/EnvDebug'));

// Lazy load test pages (only loaded in development)
const DebugUser = lazy(() => import('./pages/DebugUser'));

// Get Clerk publishable key from environment
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const clerkLocalizationOverrides = {
  signIn: {
    start: {
      title: 'Welcome back',
      subtitle: 'Please sign in to continue',
    },
  },
  organizationSwitcher: {
    personalWorkspace: 'Personal Workspace',
    action__createOrganization: 'Create New Workspace',
    notSelected: 'Select a workspace',
    organizationDropdownButton__menu: 'Workspace menu',
    organizationDropdownButton__menuTrigger: 'Open workspace menu',
  },
  createOrganization: {
    title: 'Create workspace',
    formFieldLabel__organizationName: 'Name',
    formFieldPlaceholder__organizationName: 'Workspace name',
    formFieldInputHint__organizationName: '',
    formFieldLabel__organizationSlug: 'URL slug',
    formFieldPlaceholder__organizationSlug: 'my-workspace',
    formFieldInputHint__organizationSlug: '',
    submitButton: 'Create workspace',
    invitePage: {
      formButtonPrimary: 'Continue',
    },
    subtitle: 'Set up your new workspace',
    formButtonReset: 'Cancel',
  },
  organizationList: {
    createOrganization: 'Create workspace',
    title: 'Select workspace',
    titleWithoutPersonal: 'Select workspace',
    subtitle: 'to continue to vfloow',
  },
  organizationProfile: {
    navbar: {
      title: 'Workspace Settings',
      description: 'Manage your workspace settings',
    },
    profilePage: {
      title: 'Workspace Profile',
    },
    membersPage: {
      title: 'Workspace Members',
    },
  },
  userButton: {
    action__manageAccount: 'Manage account',
    action__signOut: 'Sign out',
  },
} as const;

const App = () => {
  // Validate environment on app startup
  useEffect(() => {
    // Monitoring and Web Vitals are now initialized in main.tsx to avoid duplication

    // Register service worker for PWA support
    serviceWorkerManager.register();

    // Validate environment variables
    try {
      validateRequiredEnvVars();
      logEnvironmentStatus();

      // Validate Supabase environment (now that logger is initialized)
      if (import.meta.env.DEV) {
        validateSupabaseEnvironment();
      }
    } catch (error) {
      logger.error('❌ Environment validation failed:', error);
      // In production, you might want to show a user-friendly error page
      // For now, we'll just log the error
    }
  }, []);

  // Initialize Clerk modal customizer
  useEffect(() => {
    const observer = initializeClerkModalCustomizer();
    return () => observer.disconnect();
  }, []);

  // Create a client inside the component to fix the React hooks error
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Consider data fresh for 30 seconds
            staleTime: 30 * 1000,
            // Keep data in cache for 5 minutes
            gcTime: 5 * 60 * 1000,
            // Retry failed queries with custom logic
            retry: shouldRetryQuery,
            // Refetch on window focus
            refetchOnWindowFocus: false,
            // Don't refetch on reconnect by default
            refetchOnReconnect: false,
          },
          mutations: {
            // Global error handler for mutations
            onError: queryErrorHandler,
            // Retry failed mutations once
            retry: 1,
          },
        },
      }),
  );

  // Ensure Clerk key is configured
  if (!clerkPubKey) {
    throw new Error(
      'Clerk publishable key not found. Add VITE_CLERK_PUBLISHABLE_KEY to your .env.local file',
    );
  }

  const appContent = (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <WorkspaceProvider>
            <SelectionProvider>
              <LightingEffectsProvider>
                <DrawerProvider>
                  <PresentationModeProvider>
                    <ServiceWorkerUpdatePrompt />
                    <Toaster />
                    <Sonner
                      position="bottom-center"
                      toastOptions={{
                        style: {
                          bottom: '80px', // Move toasts up to avoid tool indicator
                        },
                      }}
                    />
                    <BrowserRouter>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* vfloow branded landing page for investors */}
                          <Route path="/" element={<LandingVfloow />} />
                          {/* Full landing page draft — DEV ONLY (VF-59: contains
                              placeholder testimonials/stats and dead CTAs; never
                              registered in a production build) */}
                          {import.meta.env.DEV && <Route path="/preview" element={<LandingV2 />} />}
                          {/* Pre-launch landing page */}
                          <Route path="/prelaunch" element={<LandingPreLaunch />} />
                          {/* Keep old landing for now */}
                          <Route path="/landing-v1" element={<Index />} />

                          {/* Auth routes */}
                          <Route path="/sign-in/*" element={<ClerkSignIn />} />
                          <Route path="/sign-up/*" element={<WaitlistSignup />} />
                          {/* Keep old routes for backwards compatibility */}
                          <Route path="/signin" element={<ClerkSignIn />} />
                          <Route path="/signup" element={<WaitlistSignup />} />

                          {/* Environment debug route — DEV ONLY (never registered in a production build) */}
                          {import.meta.env.DEV && (
                            <Route path="/env-debug" element={<EnvDebug />} />
                          )}

                          {/* Protected routes */}
                          <Route
                            path="/dashboard"
                            element={
                              <ClerkProtectedRoute>
                                <Dashboard />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/project/:id"
                            element={
                              <ClerkProtectedRoute>
                                <Project />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/workspace/settings"
                            element={
                              <ClerkProtectedRoute>
                                <WorkspaceSettings />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/integrations"
                            element={
                              <ClerkProtectedRoute>
                                <Integrations />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/integrations/portal"
                            element={
                              <ClerkProtectedRoute>
                                <PortalSettings />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings"
                            element={
                              <ClerkProtectedRoute>
                                <OrganizationSettings />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/product-library"
                            element={
                              <ClerkProtectedRoute>
                                <ProductLibrary />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/platform-admin"
                            element={
                              <ClerkProtectedRoute>
                                <PlatformAdmin />
                              </ClerkProtectedRoute>
                            }
                          />
                          {/* Unified Admin Dashboard with sub-routes */}
                          <Route
                            path="/admin/*"
                            element={
                              <ClerkProtectedRoute>
                                <AdminDashboard />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/floor-plan"
                            element={
                              <ClerkProtectedRoute>
                                <FloorPlanViewer />
                              </ClerkProtectedRoute>
                            }
                          />
                          <Route
                            path="/elevation/:elevationId"
                            element={
                              <ClerkProtectedRoute>
                                <ElevationEditor />
                              </ClerkProtectedRoute>
                            }
                          />
                          {/* Public, unauthenticated share link */}
                          <Route
                            path="/shared/floor-plans/:token"
                            element={
                              <RouteErrorBoundary>
                                <PublicFloorPlanViewer />
                              </RouteErrorBoundary>
                            }
                          />
                          {/* Debug User — DEV ONLY (never registered in a production build) */}
                          {import.meta.env.DEV && (
                            <Route path="/debug-user" element={<DebugUser />} />
                          )}
                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </BrowserRouter>
                  </PresentationModeProvider>
                </DrawerProvider>
              </LightingEffectsProvider>
            </SelectionProvider>
          </WorkspaceProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );

  // Wrap with ClerkProvider and ErrorBoundary
  return (
    <ErrorBoundary>
      <AsyncErrorBoundary>
        <ClerkProvider
          publishableKey={clerkPubKey}
          appearance={{
            baseTheme: undefined,
            variables: {
              colorPrimary: '#3b82f6',
              colorDanger: '#ef4444',
              colorSuccess: '#10b981',
              colorWarning: '#f59e0b',
              colorText: '#1f2937',
              colorTextSecondary: '#6b7280',
              colorBackground: '#ffffff',
              colorInputBackground: '#ffffff',
              colorInputText: '#1f2937',
              fontFamily:
                '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              fontSize: '16px',
              fontWeight: {
                normal: 400,
                medium: 500,
                bold: 600,
              },
              borderRadius: '0.5rem',
              spacingUnit: '1rem',
            },
            elements: {
              // Card container
              card: {
                backgroundColor: '#ffffff',
                borderRadius: '0.75rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                padding: '2.5rem',
                maxWidth: '28rem',
                width: '100%',
              },
              // Logo area
              logoBox: {
                height: 'auto',
                marginBottom: '0.25rem',
                display: 'flex',
                justifyContent: 'center',
              },
              logoImage: {
                height: 'auto',
                width: '120px',
              },
              // Main heading
              headerTitle: {
                fontSize: '1.875rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '0.5rem',
                textAlign: 'center',
              },
              headerSubtitle: {
                fontSize: '1rem',
                color: '#6b7280',
                marginBottom: '2rem',
                textAlign: 'center',
              },
              // Social buttons
              socialButtonsBlockButton: {
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#f9fafb',
                  borderColor: '#d1d5db',
                },
              },
              socialButtonsBlockButtonText: {
                color: '#374151',
                fontWeight: '500',
              },
              // Divider
              dividerRow: {
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
              },
              dividerText: {
                color: '#9ca3af',
                fontSize: '0.875rem',
              },
              // Form fields
              formFieldLabel: {
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
              },
              formFieldInput: {
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                color: '#111827',
                transition: 'all 0.2s',
                '&:focus': {
                  borderColor: '#3b82f6',
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
              },
              // Primary button
              formButtonPrimary: {
                backgroundColor: '#1f2937',
                color: '#ffffff',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                marginTop: '1.5rem',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#111827',
                },
              },
              // Footer links
              footerActionLink: {
                color: '#3b82f6',
                fontSize: '0.875rem',
                fontWeight: '500',
                '&:hover': {
                  color: '#2563eb',
                  textDecoration: 'underline',
                },
              },
              // Footer text
              footer: {
                marginTop: '2rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid #e5e7eb',
              },
              footerText: {
                color: '#6b7280',
                fontSize: '0.875rem',
                textAlign: 'center',
              },
              // Clerk branding
              internal: {
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1rem',
              },
            },
            createOrganization: {
              elements: {
                // Hide the entire slug field row
                formFieldRow__organizationSlug: {
                  display: 'none !important',
                  visibility: 'hidden',
                  height: '0',
                  overflow: 'hidden',
                  position: 'absolute',
                  left: '-9999px',
                },
                formFieldLabel__organizationSlug: {
                  display: 'none !important',
                },
                formFieldInput__organizationSlug: {
                  display: 'none !important',
                },
              },
            },
            signIn: {
              elements: {
                headerSubtitle: {
                  marginBottom: '1.5rem',
                },
              },
            },
            signUp: {
              elements: {
                headerTitle: {
                  fontSize: '1.875rem',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '0.5rem',
                  textAlign: 'center',
                },
                headerSubtitle: {
                  fontSize: '1rem',
                  color: '#6b7280',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                },
                socialButtonsBlockButton: {
                  flex: '1',
                  minWidth: '0',
                  '& span:last-child': {
                    display: 'none',
                  },
                },
                socialButtonsBlockList: {
                  display: 'flex',
                  gap: '0.75rem',
                },
              },
            },
          }}
          localization={clerkLocalizationOverrides as unknown as LocalizationResource}
        >
          {appContent}
        </ClerkProvider>
      </AsyncErrorBoundary>
    </ErrorBoundary>
  );
};

export default App;
